'use client';

import { useState, useEffect } from 'react';
import StakeholderPortals from './StakeholderPortals';
import DocumentLibrary from './DocumentLibrary';
import InquiryForm from './InquiryForm';

interface PartnershipInteractiveProps {
  className?: string;
}

const PartnershipInteractive = ({ className = '' }: PartnershipInteractiveProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedPortal, setSelectedPortal] = useState<string | null>(null);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [requestedDocument, setRequestedDocument] = useState<string | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div className={className}>
        <div className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="animate-pulse space-y-8">
              <div className="h-64 bg-muted rounded-lg" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="h-96 bg-muted rounded-lg" />
                <div className="h-96 bg-muted rounded-lg" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handlePortalSelect = (portalId: string) => {
    setSelectedPortal(portalId);
    setShowAccessModal(true);
    setTimeout(() => setShowAccessModal(false), 3000);
  };

  const handleDocumentRequest = (documentId: string) => {
    setRequestedDocument(documentId);
    setTimeout(() => setRequestedDocument(null), 3000);
  };

  return (
    <div className={className}>
      <StakeholderPortals onPortalSelect={handlePortalSelect} />
      <DocumentLibrary selectedPortal={selectedPortal} onDocumentRequest={handleDocumentRequest} />
      <InquiryForm />

      {showAccessModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg p-8 max-w-md w-full shadow-institutional animate-slide-up">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-success/10 rounded-full mb-4">
                <svg className="w-8 h-8 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="font-headline text-xl font-semibold text-foreground mb-2">
                Portal Access Granted
              </h3>
              <p className="font-body text-sm text-text-secondary">
                You now have access to stakeholder-specific documentation and resources. Scroll down to view available documents.
              </p>
            </div>
          </div>
        </div>
      )}

      {requestedDocument && (
        <div className="fixed bottom-8 right-8 bg-card border border-border rounded-lg p-6 shadow-institutional max-w-sm animate-slide-up z-50">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h4 className="font-body text-sm font-semibold text-foreground mb-1">
                Document Request Submitted
              </h4>
              <p className="font-body text-xs text-text-secondary">
                Our team will review your access request and respond within 24 hours with download credentials.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnershipInteractive;